package com.sddamico.draftmachine.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input as DomInput
import org.w3c.dom.HTMLInputElement

/**
 * A row of single-character code boxes for a 6-digit OTP. Each box is a bare styled `<input>`
 * (the DS `Input` is a full labeled field — wrong shape here). Typing a digit auto-advances,
 * Backspace on an empty box steps back and clears the prior one, and pasting the whole code
 * fills every box. The next empty box wears a copper glow; [error] paints all boxes rust.
 *
 * Controlled: [value] is the source of truth (0..[length] digits) and [onValueChange] fires on
 * every edit. [onComplete] fires with the full code once the row fills (e.g. to auto-submit); it
 * receives the code directly because the parent's state hasn't recomposed yet at that instant.
 */
@Composable
fun CodeInput(
  value: String,
  onValueChange: (String) -> Unit,
  modifier: Modifier = Modifier,
  error: Boolean = false,
  length: Int = 6,
  onComplete: ((String) -> Unit)? = null,
) {
  val inputs = remember { arrayOfNulls<HTMLInputElement>(length) }

  fun readCode(): String = (0 until length).joinToString("") { inputs[it]?.value ?: "" }

  fun emit(focusIndex: Int?) {
    focusIndex?.let { inputs[it.coerceIn(0, length - 1)]?.focus() }
    val code = readCode()
    onValueChange(code)
    if (code.length == length) onComplete?.invoke(code)
  }

  // Focus the first box on mount so the user can start typing immediately.
  LaunchedEffect(Unit) { inputs[0]?.focus() }

  // Reflect an externally-driven value change (e.g. a reset to "") into the boxes.
  LaunchedEffect(value) {
    if (value == readCode()) return@LaunchedEffect
    for (i in 0 until length) inputs[i]?.value = value.getOrNull(i)?.toString() ?: ""
  }

  DomInputRow(modifier) {
    repeat(length) { i ->
      val active = !error && i == value.length && value.length < length
      DomInput(type = InputType.Text, attrs = boxModifier(error, active).toAttrs {
        attr("inputmode", "numeric")
        attr("autocomplete", "one-time-code")
        attr("maxlength", "1")
        attr("aria-label", "Digit ${i + 1}")
        attr("value", value.getOrNull(i)?.toString() ?: "")
        ref { el ->
          inputs[i] = el
          onDispose { inputs[i] = null }
        }
        onInput { ev ->
          val digits = ev.value.filter { it.isDigit() }
          if (digits.length <= 1) {
            inputs[i]?.value = digits
            emit(if (digits.isNotEmpty() && i < length - 1) i + 1 else null)
          } else {
            var idx = i
            for (c in digits) {
              if (idx >= length) break
              inputs[idx]?.value = c.toString()
              idx++
            }
            emit(idx)
          }
        }
        onKeyDown { ev ->
          if (ev.key == "Backspace" && inputs[i]?.value.isNullOrEmpty() && i > 0) {
            ev.preventDefault()
            inputs[i - 1]?.value = ""
            emit(i - 1)
          }
        }
        onPaste { ev ->
          ev.preventDefault()
          val digits = (ev.clipboardData?.getData("text") ?: "").filter { it.isDigit() }.take(length)
          for (j in 0 until length) inputs[j]?.value = digits.getOrNull(j)?.toString() ?: ""
          emit(if (digits.length >= length) length - 1 else digits.length)
        }
      })
    }
  }
}

/**
 * The code-box row: spread edge-to-edge in the mobile frame, but center the group at the desktop
 * breakpoint (the frame widens to 800px, so [JustifyContent.SpaceBetween] would fling the boxes apart).
 */
val CodeInputRowStyle = CssStyle {
  base {
    Modifier.displayFlex().fillMaxWidth().gap(DmSpacing.space2)
      .justifyContent(JustifyContent.SpaceBetween)
  }
  Breakpoint.MD { Modifier.justifyContent(JustifyContent.Center) }
}

@Composable
private fun DomInputRow(modifier: Modifier, content: @Composable () -> Unit) {
  org.jetbrains.compose.web.dom.Div(
    attrs = CodeInputRowStyle.toModifier().then(modifier).toAttrs()
  ) { content() }
}

/** The stamped surface for one code box — sunken slot, copper when active, rust when errored. */
private fun boxModifier(error: Boolean, active: Boolean): Modifier {
  val base = Modifier
    .styleModifier {
      property("box-sizing", "border-box")
      property("outline", "none")
      property("text-align", "center")
      property("padding", "0")
    }
    .width(50.px)
    .height(58.px)
    .borderRadius(DmEffects.radiusSm)
    .backgroundColor(DmColor.surfaceInset)
    .fontFamily(*DmType.mono)
    .fontSize(24.px)
    .fontWeight(DmType.weightBold)
    .color(DmColor.text)
    .boxShadowRaw(DmEffects.shadowInset)

  return when {
    error -> base
      .border(1.px, LineStyle.Solid, DmColor.rust500)
      .backgroundColor(Color.rgba(180, 71, 42, 0.08f))
      .color(DmColor.rust300)
    active -> base
      .border(2.px, LineStyle.Solid, DmColor.brass400)
      .boxShadowRaw("${DmEffects.shadowInset}, ${DmEffects.glowBrass}")
    else -> base
      .border(1.px, LineStyle.Solid, DmColor.borderStrong)
  }
}
